import { lazy, Suspense, useEffect, useState } from "react";
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Topbar } from "@/components/Topbar";
import { CommandPalette } from "@/components/CommandPalette";
import { ShortcutHelpDialog } from "@/components/ShortcutHelpDialog";
import { GlossaryDialog } from "@/components/GlossaryDialog";
import { Toaster } from "@/components/ui/Toaster";
import { LiveAnnouncer } from "@/components/ui/LiveAnnouncer";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { DebugOverlay } from "@/components/DebugOverlay";
import { ConnectingSplash } from "@/components/ConnectingSplash";
import { IncidentStrip } from "@/components/IncidentStrip";
import { ChartSkeleton } from "@/components/ui/ChartSkeleton";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { useAlarmWatcher } from "@/hooks/useAlarmWatcher";
import { carryTo } from "@/lib/carryParams";
import { useTopology } from "@/store/site";
import { readUiPref, HOME_VIEW_PREF } from "@/store/uiPrefs";
import { VALID_ROUTE_PREFIXES, isValidHomeView } from "@/lib/routes";
import OverviewPage from "@/pages/OverviewPage";
import EnergyFlowPage from "@/pages/EnergyFlowPage";
import BessPage from "@/pages/BessPage";
import BessDetailPage from "@/pages/BessDetailPage";
import AlarmsPage from "@/pages/AlarmsPage";
import AlarmRulesPage from "@/pages/AlarmRulesPage";
import SchedulePage from "@/pages/SchedulePage";
import ControllerPage from "@/pages/ControllerPage";
import SiteConfigPage from "@/pages/SiteConfigPage";

/**
 * Code-splitting (XTRA-09): the heavy analysis pages are lazy so the
 * landing + incident paths (Overview / Flow / BESS / Alarms / Schedule /
 * Controller / Site — kept eager above) never wait on them. Before this
 * split the SPA shipped as ONE 1.9 MB chunk that had to parse before the
 * ConnectingSplash could even resolve, on every TB dashboard open.
 * ECharts itself is additionally split into its own chunk via
 * `manualChunks` in vite.config.ts.
 */
const HistoricalPage = lazy(() => import("@/pages/HistoricalPage"));
const AnalyticsPage = lazy(() => import("@/pages/AnalyticsPage"));
const RevenuePage = lazy(() => import("@/pages/RevenuePage"));
const MarketPricePage = lazy(() => import("@/pages/MarketPricePage"));
const ScadaTestPage = lazy(() => import("@/pages/ScadaTestPage"));

/**
 * Suspense fallback while a lazy page chunk streams in — mirrors the
 * PageShell layout (same paddings, a title-row stub, then chart-sized
 * blocks) so the swap to real content doesn't jump.
 */
function PageFallback() {
  return (
    <div aria-busy="true" className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="space-y-4">
        <ChartSkeleton height={28} className="max-w-56" />
        <ChartSkeleton height={260} />
        <ChartSkeleton height={260} />
      </div>
    </div>
  );
}

/**
 * Map the `?mode=…` query param (set by the tb-host-widget via its
 * widget-settings dropdown — TB-EMS / ems-dashboard pattern) to one of
 * our SPA routes. Lets a single ThingsBoard widget host either the
 * ems-dashboard or operator-spa, with admins picking the landing tab
 * per widget instance.
 *
 * Unknown / missing mode values fall through — the SPA stays on whatever
 * route the browser already requested (or `/` → redirect to `/overview`).
 */
const MODE_TO_ROUTE: Record<string, string> = {
  // Operator-spa-native mode names (preferred — admins set these explicitly
  // for our widget instance).
  "bess-overview": "/overview",
  "bess-flow":     "/flow",
  "bess-fleet":    "/bess",
  "bess-history":  "/historical",
  "bess-analytics": "/analytics",
  "bess-revenue":  "/revenue",
  "bess-market-price": "/market-price",
  "bess-alarms":   "/alarms",
  "bess-alarm-rules": "/alarms/rules",
  "bess-schedule": "/schedule",
  "bess-controller": "/controller",
  "bess-site":     "/site",
  // ems-dashboard mode names that have a sensible operator-spa fallback,
  // in case the same widget instance is mistakenly re-pointed at our
  // SPA without the mode being updated.
  "energy-monitor": "/overview",
  "comparison":     "/historical",
  "alarm-inspection": "/alarms",
  "energy-prices":  "/market-price",
};

/**
 * Resolve the `?mode=` deep-link param to a route. Shared by `RootRedirect`
 * (synchronous, for a bare `/` load) and `ModeRouter` (effect-based, for
 * `?mode=` arriving on any other path).
 *
 * A non-empty unknown value warns (once) instead of failing silently, so a
 * misconfigured TB widget instance is diagnosable from the console (NAV-08).
 */
let warnedUnknownMode = false;
function resolveModeRoute(): string | null {
  const mode = new URLSearchParams(window.location.search).get("mode");
  if (!mode) return null;
  const route = MODE_TO_ROUTE[mode];
  if (!route) {
    if (!warnedUnknownMode) {
      warnedUnknownMode = true;
      console.warn(
        `[operator-spa] Unknown ?mode= value "${mode}" — falling back to the requested route. Known modes: ${Object.keys(MODE_TO_ROUTE).join(", ")}`,
      );
    }
    return null;
  }
  return route;
}

/**
 * Resolve the landing route for a "/" load (or a ?mode= deep link),
 * applying the saved-route-vs-mode precedence in ONE place. A fresh saved
 * route from the SAME boot ?mode= beats the mode's landing view; otherwise
 * the mode route (or /overview) wins. Pure — returns only the target string;
 * callers own the navigation mechanic (sync <Navigate> vs effect navigate()).
 */
function resolveLanding(): string {
  const saved = readLastRoute();
  if (saved && saved.mode === BOOT_MODE) return saved.route;
  return resolveModeRoute() ?? "/overview";
}

/**
 * Widget re-instantiation recovery (DOCS-02): the TB host rebuilds the
 * iframe on dashboard re-renders, layout edits and full reloads, and the
 * operator used to be dumped back on the `?mode=` landing view with all
 * context lost. `RouteMemory` persists the last in-app route to
 * localStorage; `RootRedirect` / `ModeRouter` restore it on the next boot
 * — but only while the widget still boots with the SAME `?mode=` the
 * route was saved under, so an admin re-pointing the widget at a
 * different landing view still wins.
 */
const LAST_ROUTE_KEY = "ems-last-route";
const LAST_ROUTE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // saved routes go stale after a week

// Route allow-list + home-view validator now live in lib/routes.ts so the
// CommandPalette can reuse isValidHomeView without importing App.tsx (cycle).
// Re-exported here so existing `@/App` importers keep working.
export { VALID_ROUTE_PREFIXES, isValidHomeView };

// The `?mode=` this instance booted with — captured at module load, before
// any in-app navigation can strip it from the address bar.
const BOOT_MODE = new URLSearchParams(window.location.search).get("mode") ?? "";

type LastRoute = { mode: string; route: string; ts: number };

function readLastRoute(): LastRoute | null {
  try {
    const raw = localStorage.getItem(LAST_ROUTE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<LastRoute>;
    // Reject malformed routes — and "/" itself, which would redirect to
    // itself forever from RootRedirect.
    if (typeof v.route !== "string" || !v.route.startsWith("/") || v.route === "/") return null;
    if (typeof v.ts !== "number" || Date.now() - v.ts > LAST_ROUTE_MAX_AGE_MS) return null;
    return { mode: typeof v.mode === "string" ? v.mode : "", route: v.route, ts: v.ts };
  } catch {
    // Sandboxed-iframe storage can throw — recovery is best-effort.
    return null;
  }
}

function RouteMemory() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    // "/" is a transient redirect stop and /scada-test is a dev harness —
    // neither is a place worth restoring an operator to. Unknown paths
    // (NotFound) are never persisted either.
    if (pathname === "/" || pathname === "/scada-test") return;
    if (!VALID_ROUTE_PREFIXES.some((p) => pathname.startsWith(p))) return;
    try {
      localStorage.setItem(
        LAST_ROUTE_KEY,
        JSON.stringify({ mode: BOOT_MODE, route: pathname + search, ts: Date.now() }),
      );
    } catch {
      // best-effort (see readLastRoute)
    }
  }, [pathname, search]);
  return null;
}

/**
 * Landing redirect for a bare `/` load. Honours `?mode=` SYNCHRONOUSLY
 * during render so it can't race the effect-based `ModeRouter` — both
 * resolve to the same target. (A blind `<Navigate to="/overview">` here
 * used to clobber ModeRouter's deep-link redirect.)
 *
 * Precedence (DOCS-02 + F23), top to bottom — admin ?mode= MUST still beat
 * a personal home view:
 *   1. return-to-where-you-were (saved route from the SAME boot ?mode=)
 *   2. admin ?mode= deep link (resolveModeRoute)
 *   3. optional per-site personal home.view pref (validated; stale falls back)
 *   4. hardcoded /overview default
 * Tiers 1+2 also back resolveLanding() (used by ModeRouter); the home.view
 * pref is intentionally NOT consulted there — it only matters on a bare "/"
 * load with no ?mode=, which this component already owns.
 */
function RootRedirect() {
  const siteId = useTopology().id;
  const saved = readLastRoute();
  if (saved && saved.mode === BOOT_MODE) return <Navigate to={saved.route} replace />; // 1. return-to-where-you-were
  const modeRoute = resolveModeRoute();
  if (modeRoute) return <Navigate to={modeRoute} replace />;                            // 2. admin ?mode= — wins over a personal home
  const home = readUiPref<string>(siteId, HOME_VIEW_PREF, "");                          // 3. optional personal home view
  if (home && isValidHomeView(home)) return <Navigate to={home} replace />;
  return <Navigate to="/overview" replace />;                                           // 4. hardcoded default
}

function ModeRouter() {
  const navigate = useNavigate();
  useEffect(() => {
    const target = resolveModeRoute();
    if (target) {
      // Only fires when a ?mode= is present (target != null). The
      // saved-route-vs-mode precedence now lives in resolveLanding(), which
      // recomputes resolveModeRoute() internally and returns the same target
      // here — no more hand-maintained "the two MUST agree" invariant.
      navigate(resolveLanding(), { replace: true });
    }
    // Only honour the param on initial mount — once routed, user
    // navigation takes over.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

/**
 * Catch-all for unknown URLs (XTRA-10). The old `<Navigate to="/overview">`
 * rewrite destroyed the evidence of a typo'd / stale deep link — the
 * operator believed they were looking at the page they asked for. This slim
 * card keeps the bad path visible (and in the address bar) so broken links
 * are noticed and reportable, with carry-aware escape links to the two
 * places an operator actually needs. Deliberately PageShell-less: rendering
 * tabs + freshness chrome around a non-page would suggest it IS a page.
 */
function NotFound() {
  const { pathname } = useLocation();
  return (
    <div className="mx-auto mt-16 w-full max-w-md px-4">
      <div className="card p-6 text-center">
        <div className="text-sm font-semibold text-text">Unknown page</div>
        <div className="mt-1 break-all font-mono text-xs text-text-muted">{pathname}</div>
        <p className="mt-3 text-xs text-text-muted">
          This address doesn&apos;t match any view — the link may be mistyped or from an
          older release.
        </p>
        <div className="mt-4 flex items-center justify-center gap-3 text-sm">
          <Link className="font-medium text-accent hover:underline" to={carryTo("/overview")}>
            Go to Overview
          </Link>
          <span aria-hidden="true" className="text-text-faint">
            ·
          </span>
          <Link className="font-medium text-accent hover:underline" to={carryTo("/alarms")}>
            Go to Alarms
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  useKeyboardShortcuts({
    onPaletteOpen: () => setPaletteOpen(true),
    onShortcutsOpen: () => setShortcutsOpen(true),
    onSearchFocus: () => {
      const el = document.querySelector<HTMLInputElement>("[data-spa-search='1']");
      el?.focus();
      el?.select();
    },
  });

  useAlarmWatcher();

  // Dev-only palette-contract check (G22): console.warns if any two semantic
  // meanings resolve to the same hue under any theme. Dynamically imported
  // behind import.meta.env.DEV so it ships zero bytes to the production bundle.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    let cancelled = false;
    void import("@/lib/paletteContract").then((m) => { if (!cancelled) m.assertPaletteContract(); });
    return () => { cancelled = true; };
  }, []);

  // TB-EMS-aligned layout: NO left rail. ThingsBoard already provides a
  // vertical sidebar at the host level; nesting our own inside the
  // iframe was confusing for operators. Navigation is entirely top-tab
  // driven (rendered by Topbar) so the SPA stays slim and the host
  // sidebar remains the canonical "where am I in TB" affordance.
  return (
    <ErrorBoundary>
      <div className="relative flex min-h-screen flex-col bg-bg text-text">
        <ModeRouter />
        <RouteMemory />
        <Topbar
          onPaletteOpen={() => setPaletteOpen(true)}
          onShortcutsOpen={() => setShortcutsOpen(true)}
        />
        <main className="relative flex-1 overflow-x-hidden">
          {/* Hides mock data behind a "Connecting to TB" overlay while
              the embedded adapter is being constructed. Only mounts in
              live mode (mode === "live" && connection !== "live"). */}
          <ConnectingSplash />
          {/* Critical-incident strip (F24): a slim shell-level surface that
              promotes the worst active critical alarm to every route except
              /overview (HealthBanner covers that). Self-suppresses to null
              when there is no active critical, so it's free to mount here. */}
          <IncidentStrip />
          <ErrorBoundary>
            <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route path="/" element={<RootRedirect />} />
                <Route path="/overview" element={<OverviewPage />} />
                <Route path="/flow" element={<EnergyFlowPage />} />
                <Route path="/bess" element={<BessPage />} />
                <Route path="/bess/:id" element={<BessDetailPage />} />
                <Route path="/historical" element={<HistoricalPage />} />
                <Route path="/analytics" element={<AnalyticsPage />} />
                <Route path="/revenue" element={<RevenuePage />} />
                <Route path="/market-price" element={<MarketPricePage />} />
                <Route path="/alarms" element={<AlarmsPage />} />
                <Route path="/alarms/rules" element={<AlarmRulesPage />} />
                <Route path="/schedule" element={<SchedulePage />} />
                <Route path="/controller" element={<ControllerPage />} />
                <Route path="/site" element={<SiteConfigPage />} />
                {/* Dev harness with hand-crafted synthetic topologies —
                    registered in dev builds only (NAV-12), so a mistyped
                    production URL can never land an operator on fake data.
                    In production the path falls through to NotFound. */}
                {import.meta.env.DEV && (
                  <Route path="/scada-test" element={<ScadaTestPage />} />
                )}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </main>
        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
        <ShortcutHelpDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
        <GlossaryDialog />
        <Toaster />
        <LiveAnnouncer />
        <DebugOverlay />
      </div>
    </ErrorBoundary>
  );
}
